/**
 * El enrutador · TanStack Router, en código y no por ficheros
 *
 * ## Por qué ahora
 *
 * `club/secciones.ts` llevaba escrita la regla desde el primer día: **con la sexta
 * sección de primer nivel entra el enrutador**. Se cruzó con «Grados» el
 * 17-09-2026 y esto es el pago de esa deuda. Lo que había era `?seccion=` con una
 * cadena de `if`, que aguantaba cinco pantallas y no seis con subpestañas.
 *
 * ## Por qué en código y no por ficheros
 *
 * El enrutado por ficheros de TanStack genera un `routeTree.gen.ts` que hay que
 * mantener sincronizado y que entra en el repositorio. Es el mismo artefacto que
 * `shared/api/client.ts` rechaza para el cliente de la API —«los dos añaden un
 * artefacto que hay que mantener sincronizado»—, y con veinte rutas el árbol en
 * código cabe en un fichero que se lee de arriba abajo.
 *
 * ## Las pantallas no saben que hay un enrutador
 *
 * Cada componente sigue recibiendo `alAbrir`, `alCerrar` y compañía, y son **estas
 * rutas** las que los traducen a `navigate`. Tres razones, y la tercera es la que
 * importa:
 *
 * 1. El cambio es acotado: ninguna pantalla se tocó al meter esto.
 * 2. Se prueban sin montar un enrutador.
 * 3. El día que se cambie de enrutador —o se quite— las pantallas no se enteran.
 *    Un `useNavigate()` dentro de cada componente habría atado veinte ficheros a
 *    una dependencia.
 *
 * ## El club elegido sigue en la query
 *
 * `?club=` **no** es parte de la ruta, y es deliberado: la misma pantalla existe
 * para cualquier club, y quien tiene dos membresías cambia de club sin cambiar de
 * sitio. Meterlo en la ruta obligaría a reescribir la URL entera en cada cambio de
 * club y a repetir el identificador en veinte sitios.
 */
/*
 * `throw redirect(...)` es el patrón de TanStack Router: la redirección viaja como
 * una excepción para cortar el render de la ruta que no toca. No es un `Error` y
 * por eso la regla se queja, pero lanzar otra cosa aquí sería inventarse una API.
 */
/* eslint-disable @typescript-eslint/only-throw-error */
import { useEffect, useState, type ReactNode } from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
  Outlet,
  redirect,
  useNavigate,
  useParams,
} from '@tanstack/react-router';
import { Acceso } from '@/acceso/Acceso';
import { Canje } from '@/acceso/Canje';
import { Invitacion } from '@/acceso/Invitacion';
import { Restablecer } from '@/acceso/Restablecer';
import { VerificarCorreo } from '@/acceso/VerificarCorreo';
import { MiCuenta } from '@/cuenta/MiCuenta';
import { useSesion, useYo } from '@/shared/auth/sesionContexto';
import { ConSesion } from '@/shared/auth/ConSesion';
import { Cargando, Vacio } from '@/shared/ui/Estados';
import {
  aplicacionDeEsteDominio,
  dominioDe,
  enSuDominio,
  type Aplicacion,
} from '@/shared/dominios';
import { MarcoDelPanel } from '@/club/MarcoDelPanel';
import {
  puedeCerrarUnaSede,
  puedeEditarElClub,
  puedeEditarMaterial,
  puedeVerCompeticion,
  puedeVerLicencias,
  puedeFirmarElContrato,
  puedeGestionarPersonal,
  puedeEditarElTemario,
  puedeOtorgarGrados,
  puedeQuitarDelTemario,
  puedeVerInformesDeGestion,
} from '@/club/permisos';
import {
  MarcoDelBackOffice,
  useContextoDelBackOffice,
} from '@/backoffice/Marco';
import { ListaDeAlumnos } from '@/club/alumnos/ListaDeAlumnos';
import { MarcoDeAlumnos } from '@/club/alumnos/MarcoDeAlumnos';
import { ClasesDePrueba } from '@/club/directorio/ClasesDePrueba';
import { Embudo } from '@/club/informes/Embudo';
import { Comparativa } from '@/club/informes/Comparativa';
import { MarcoDeMaterial } from '@/club/material/MarcoDeMaterial';
import { Catalogo } from '@/club/material/Catalogo';
import { Pedidos } from '@/club/material/Pedidos';
import { Solicitudes } from '@/club/material/Solicitudes';
import { SegundaMano } from '@/club/material/SegundaMano';
import { MarcoDeCompeticion } from '@/club/competicion/MarcoDeCompeticion';
import { Campeonatos } from '@/club/competicion/Campeonatos';
import { NoIncluido } from '@/club/NoIncluido';
import type { FuncionDelPlan } from '@/club/plan';
import { FichaDeCampeonato } from '@/club/competicion/FichaDeCampeonato';
import { Licencias } from '@/club/competicion/Licencias';
import { FichaPublica } from '@/club/directorio/FichaPublica';
import { FichaDeAlumno } from '@/club/alumnos/FichaDeAlumno';
import { ListaDeGrupos } from '@/club/grupos/ListaDeGrupos';
import { FichaDeGrupo } from '@/club/grupos/FichaDeGrupo';
import { Calendario } from '@/club/calendario/Calendario';
import { MarcoDelCalendario } from '@/club/calendario/MarcoDelCalendario';
import { AvisosDeAusencia } from '@/club/calendario/AvisosDeAusencia';
import { Asistencia as AsistenciaDelCalendario } from '@/club/calendario/Asistencia';
import { Avisos } from '@/club/avisos/Avisos';
import { AcusesDeAviso } from '@/club/avisos/AcusesDeAviso';
import { MarcoDeComunicacion } from '@/club/avisos/MarcoDeComunicacion';
import { Chat } from '@/club/avisos/Chat';
import { Muro } from '@/club/avisos/Muro';
import { HorarioDeSilencio } from '@/club/avisos/HorarioDeSilencio';
import { SistemasDeGrados } from '@/club/grados/SistemasDeGrados';
import { EscaleraDeGrados } from '@/club/grados/EscaleraDeGrados';
import { MarcoDeGrados } from '@/club/grados/MarcoDeGrados';
import { Examenes } from '@/club/grados/Examenes';
import { FichaDeExamen } from '@/club/grados/FichaDeExamen';
import { Temario } from '@/club/grados/Temario';
import { Tarifas } from '@/club/cuotas/Tarifas';
import { CatalogoDeDescuentos } from '@/club/descuentos/CatalogoDeDescuentos';
import { ListaDeRecibos } from '@/club/facturacion/ListaDeRecibos';
import { FichaDeRecibo } from '@/club/facturacion/FichaDeRecibo';
import { Morosos } from '@/club/facturacion/Morosos';
import { Remesa } from '@/club/facturacion/Remesa';
import { Devoluciones } from '@/club/facturacion/Devoluciones';
import { LoCobrado } from '@/club/facturacion/LoCobrado';
import { ListaDePagadores } from '@/club/pagadores/ListaDePagadores';
import { FichaDePagador } from '@/club/pagadores/FichaDePagador';
import { Clubes } from '@/backoffice/Clubes';
import { FichaDeClub } from '@/backoffice/FichaDeClub';
import { AvisosDePlanes } from '@/backoffice/AvisosDePlanes';
import { CobrosDePlataforma } from '@/backoffice/Cobros';
import { Soporte } from '@/backoffice/Soporte';
import { Procesos } from '@/backoffice/Procesos';
import { MarcoDeSeccion } from '@/backoffice/MarcoDeSeccion';
import { Metricas } from '@/backoffice/Metricas';
import { Planes } from '@/backoffice/Planes';
import { TrazasDeAvisos } from '@/backoffice/TrazasDeAvisos';
import { SaludDeLosClubes } from '@/backoffice/SaludDeLosClubes';
import { Accesos } from '@/backoffice/Accesos';
import { Webhooks } from '@/backoffice/Webhooks';
import { EquipoDeKiap } from '@/backoffice/EquipoDeKiap';
import { SolicitudesRgpd } from '@/backoffice/SolicitudesRgpd';
import { InterruptoresGlobales } from '@/backoffice/InterruptoresGlobales';
import { Comercial } from '@/backoffice/Comercial';
import { CatalogoDeDisciplinas } from '@/backoffice/CatalogoDeDisciplinas';
import { CatalogoDeGrados } from '@/backoffice/CatalogoDeGrados';
import { CatalogoDeConsentimientos } from '@/backoffice/CatalogoDeConsentimientos';
import { Reclamaciones } from '@/backoffice/Reclamaciones';
import { BajasDelDirectorio } from '@/backoffice/BajasDelDirectorio';
import { ImportarCenso } from '@/backoffice/ImportarCenso';
import { Comparativas } from '@/backoffice/Comparativas';
import { EstadoDelServicio } from '@/backoffice/Estado';
import { Hoy } from '@/backoffice/Hoy';
import { useContextoDelClub } from '@/club/contexto';
import {
  VISTAS_DE_CATALOGOS,
  VISTAS_DE_DINERO,
  VISTAS_DE_DIRECTORIO,
  VISTAS_DE_OPERACION,
  VISTAS_DE_SOPORTE,
} from '@/backoffice/secciones';
import { MarcoDeCobros } from '@/club/facturacion/MarcoDeCobros';
import { MarcoDelClub } from '@/club/ajustes/MarcoDelClub';
import { DatosDelClub } from '@/club/ajustes/DatosDelClub';
import { VuestroPlan } from '@/club/ajustes/VuestroPlan';
import { Sedes } from '@/club/ajustes/Sedes';
import { Disciplinas } from '@/club/ajustes/Disciplinas';
import { Personal } from '@/club/ajustes/Personal';
import { Marca } from '@/club/ajustes/Marca';
import { Interruptores } from '@/club/ajustes/Interruptores';
import { DocumentosDePersonal } from '@/club/ajustes/DocumentosDePersonal';
import { MarcoDeCumplimiento } from '@/club/cumplimiento/MarcoDeCumplimiento';
import { Consentimientos } from '@/club/cumplimiento/Consentimientos';
import { RegistroDeAccesos } from '@/club/cumplimiento/RegistroDeAccesos';
import { ModoSoporte } from '@/club/cumplimiento/ModoSoporte';
import { ContratoDeEncargo } from '@/club/cumplimiento/ContratoDeEncargo';
import { Inicio } from '@/club/inicio/Inicio';
import { SolicitudesDeDatos } from '@/club/cumplimiento/SolicitudesDeDatos';
import { Exportacion } from '@/club/cumplimiento/Exportacion';
import { MarcoDeInformes } from '@/club/informes/MarcoDeInformes';
import { Asistencia } from '@/club/informes/Asistencia';
import { Ingresos } from '@/club/informes/Ingresos';
import { Bajas } from '@/club/informes/Bajas';
import { Rentabilidad } from '@/club/informes/Rentabilidad';
import { Ocupacion } from '@/club/informes/Ocupacion';
import { PrevisionDeCaja } from '@/club/informes/PrevisionDeCaja';
import { HorasDeEntrenador } from '@/club/informes/HorasDeEntrenador';

/* ── La raíz ──────────────────────────────────────────────────────────────── */

const raiz = createRootRoute({ component: Outlet });

/**
 * El acceso, y el canje de un enlace mágico.
 *
 * Va aparte de todo y **sin sesión**: quien llega con `?token=` viene del correo y
 * todavía no tiene ninguna.
 */
const acceso = createRoute({
  getParentRoute: () => raiz,
  path: '/acceso',
  component: function PantallaDeAcceso() {
    const { token } = acceso.useSearch();
    const { estado } = useSesion();

    // El canje va primero: quien llega con `?token=` viene del correo.
    if (token) return <Canje token={token} />;

    /*
     * Quien ya tiene sesión y aterriza en `/acceso` —un enlace viejo, el botón de
     * atrás— pasa por la puerta, que le manda a su aplicación. No hay bucle
     * posible: `ConSesion` **no navega**, pinta el acceso donde esté.
     */
    if (estado.fase === 'dentro') return <Navigate to="/" replace />;

    return <Acceso />;
  },
  validateSearch: (b: Record<string, unknown>): { token?: string } =>
    typeof b['token'] === 'string' ? { token: b['token'] } : {},
});

/**
 * La puerta: decide qué aplicación abre esta cuenta.
 *
 * `can_open` **viene resuelto del servidor** y no se recalcula aquí (`BFF_BO.md`
 * §5.1). El orden importa: quien tiene las dos cosas —nosotros, con un club de
 * pruebas— entra al back office, que es donde trabaja.
 */
function Puerta() {
  const { estado, salir } = useSesion();

  if (estado.fase === 'arrancando') {
    return <Cargando filas={2} etiqueta="Comprobando la sesión" />;
  }

  if (estado.fase === 'fuera') return <Acceso />;

  const puede = (que: string) => estado.yo.can_open.includes(que);
  /*
   * Con el back office en su propio dominio (02-11-2026), cada dirección abre solo
   * lo suyo, aunque la cuenta pueda las dos cosas: es lo que separa las dos
   * pestañas. En local (`null`) sirve las dos, como antes.
   */
  const aqui = aplicacionDeEsteDominio();

  if (aqui === 'backoffice') {
    if (puede('backoffice')) return <Navigate to="/admin/hoy" replace />;
    return (
      <EnOtraDireccion
        titulo="Esto es el back office de Kiap"
        explicacion={
          puede('panel')
            ? `Es la dirección del equipo de Kiap. El panel de tu club está en ${dominioDe('panel')}.`
            : 'Es la dirección del equipo de Kiap, y tu cuenta no tiene acceso.'
        }
        destino={puede('panel') ? 'panel' : null}
        salir={salir}
      />
    );
  }

  // `replace` para que «atrás» no devuelva a esta puerta y deje al usuario
  // rebotando entre ella y su aplicación.
  // Al back office, a «Hoy»: la rutina de cada mañana ya contada (13-10-2026).
  if (aqui === null && puede('backoffice')) {
    return <Navigate to="/admin/hoy" replace />;
  }
  // A Inicio, con lo pendiente arriba (13-10-2026). Antes, la lista de alumnos.
  if (puede('panel')) return <Navigate to="/inicio" replace />;

  if (aqui === 'panel' && puede('backoffice')) {
    return (
      <EnOtraDireccion
        titulo="El back office tiene su propia dirección"
        explicacion={`Desde el 2 de noviembre de 2026 está en ${dominioDe('backoffice')}. Esta es la del panel de los clubes, y tu cuenta no tiene ningún club.`}
        destino="backoffice"
        salir={salir}
      />
    );
  }

  /*
   * Tiene sesión y no abre ni el panel ni el back office: un padre o un entrenador,
   * cuyo sitio son las apps. Y **con salida**, porque sin ella se queda atrapado:
   * sesión válida, ninguna pantalla y ningún botón. Pasó de verdad.
   */
  return (
    <main className="lienzo">
      <Vacio
        titulo="Esta dirección no es para tu cuenta"
        explicacion={
          puede('coach') || puede('family')
            ? 'Tu cuenta es de la aplicación de familias o de maestro, no del panel del club. Ábrela desde el móvil.'
            : 'Tu cuenta no tiene todavía acceso a ningún club. Si acabas de recibir una invitación, ábrela desde el correo.'
        }
        accion={
          <button type="button" onClick={() => void salir()}>
            Salir y entrar con otra cuenta
          </button>
        }
      />
    </main>
  );
}

/**
 * Una cuenta en la dirección que no es la suya, con el botón para ir a la buena.
 *
 * Y con salida, como la puerta: quien llegó aquí con otra cuenta tiene que poder
 * cambiarla sin saber dónde se cierra la sesión.
 */
function EnOtraDireccion({
  titulo,
  explicacion,
  destino,
  salir,
}: {
  titulo: string;
  explicacion: string;
  destino: Aplicacion | null;
  salir: () => Promise<void>;
}) {
  return (
    <main className="lienzo">
      <Vacio
        titulo={titulo}
        explicacion={explicacion}
        accion={
          <>
            {destino && (
              <button
                type="button"
                onClick={() =>
                  window.location.assign(enSuDominio(destino, '/'))
                }
              >
                Ir a {dominioDe(destino)}
              </button>
            )}
            <button
              type="button"
              className="btn-plano"
              onClick={() => void salir()}
            >
              Salir y entrar con otra cuenta
            </button>
          </>
        }
      />
    </main>
  );
}

/**
 * Lleva a la misma ruta en el dominio de su aplicación · 02-11-2026
 *
 * Al abrir la página no llega a pintarse: `main.tsx` se va al otro dominio antes de
 * montar nada (`rutaEnOtroDominio`), porque arrancar la sesión y saltar con la
 * renovación en vuelo la corta. Esto cubre lo que queda, una navegación **dentro**
 * de la aplicación hacia una ruta del otro lado, con la sesión ya arrancada.
 *
 * `replace` y no `assign`: «atrás» no tiene que volver a una dirección que solo
 * redirige.
 */
function IrASuDominio({ aplicacion }: { aplicacion: Aplicacion }) {
  const { pathname, search, hash } = window.location;
  const destino = enSuDominio(aplicacion, `${pathname}${search}${hash}`);

  useEffect(() => {
    window.location.replace(destino);
  }, [destino]);

  return <Cargando filas={1} etiqueta={`Abriendo ${dominioDe(aplicacion)}`} />;
}

/**
 * Las tres pantallas que abren los correos del servidor · §C.1
 *
 * `/invitacion`, `/restablecer` y `/verificar-correo` **no son direcciones que se
 * eligieran aquí**: las escribe el backend en el cuerpo de cada correo
 * (`InviteMemberToClub`, `PasswordReset`, `EnsureAccount`). Igual que
 * `/clubes/{slug}` en la landing, renombrar una convierte en 404 un enlace que ya
 * está en la bandeja de entrada de alguien.
 *
 * Las tres van sin sesión y comparten la misma validación de `?token=`: si no viene,
 * no hay nada que hacer y se manda a la puerta.
 */
const soloToken = (b: Record<string, unknown>): { token?: string } =>
  typeof b['token'] === 'string' ? { token: b['token'] } : {};

/**
 * `?nuevo=1`: la pantalla se abre con su formulario de alta ya abierto · 13-10-2026
 *
 * Lo usan los accesos rápidos de Inicio. «Añadir un alumno» que lleva a la lista y
 * obliga a buscar otra vez el botón es un acceso que no ahorra nada. La pantalla
 * **se lo come al llegar** (`replace`): si no, «atrás» desde la ficha del alumno
 * recién creado volvería a abrir el formulario.
 */
const conNuevo = (b: Record<string, unknown>): { nuevo?: 1 } =>
  b['nuevo'] === 1 || b['nuevo'] === '1' ? { nuevo: 1 } : {};

/**
 * `?nuevo=1&volver=/grupos?nuevo=1`: abrir el alta y, al guardar, volver · 03-11-2026
 *
 * Lo usa `FaltaAntes`: quien crea un grupo sin tener sede va a crear la sede y, al
 * guardarla, vuelve al alta del grupo con la sede ya elegida. `volver` solo admite
 * una ruta de este mismo panel —empieza por una barra y no por dos—, para que nadie
 * pueda fabricar un enlace del panel que acabe en otra web.
 */
export const conNuevoYVolver = (
  b: Record<string, unknown>
): { nuevo?: 1; volver?: string } => {
  const volver = b['volver'];
  return {
    ...conNuevo(b),
    ...(typeof volver === 'string' &&
    volver.startsWith('/') &&
    !volver.startsWith('//') &&
    !volver.includes('\\')
      ? { volver }
      : {}),
  };
};

/**
 * Lo que cada pantalla con `conNuevoYVolver` necesita: si abrir el alta, y qué hacer
 * al guardar. Se come `nuevo` al llegar (como `conNuevo`) y conserva `volver`.
 */
function useNuevoYVolver(
  busqueda: { nuevo?: 1; volver?: string },
  aqui: string
) {
  const ir = useNavigate();
  const { nuevo, volver } = busqueda;
  useEffect(() => {
    if (nuevo) {
      void ir({ to: aqui, search: volver ? { volver } : {}, replace: true });
    }
  }, [nuevo, volver, aqui, ir]);
  return {
    empezarCreando: nuevo === 1,
    alCrear: volver ? () => void ir({ href: volver }) : undefined,
  };
}

const invitacion = createRoute({
  getParentRoute: () => raiz,
  path: '/invitacion',
  validateSearch: soloToken,
  component: function PantallaDeInvitacion() {
    const { token } = invitacion.useSearch();
    if (!token) return <Navigate to="/" replace />;
    return <Invitacion token={token} />;
  },
});

const restablecer = createRoute({
  getParentRoute: () => raiz,
  path: '/restablecer',
  validateSearch: soloToken,
  component: function PantallaDeRestablecer() {
    const { token } = restablecer.useSearch();
    if (!token) return <Navigate to="/" replace />;
    return <Restablecer token={token} />;
  },
});

const verificarCorreo = createRoute({
  getParentRoute: () => raiz,
  path: '/verificar-correo',
  validateSearch: soloToken,
  component: function PantallaDeVerificarCorreo() {
    const { token } = verificarCorreo.useSearch();
    if (!token) return <Navigate to="/" replace />;
    return <VerificarCorreo token={token} />;
  },
});

/**
 * §C.2 · «Mi cuenta», y cuelga de la raíz a propósito.
 *
 * No es del club ni del back office: **quien tiene dos clubes tiene una sola cuenta**,
 * y meterla dentro de `/panel` la ataría al club elegido. Por eso se entra desde la
 * cabecera —donde está el correo— y se vuelve al sitio del que se vino.
 */
const miCuenta = createRoute({
  getParentRoute: () => raiz,
  path: '/mi-cuenta',
  component: function PantallaDeMiCuenta() {
    const ir = useNavigate();
    return (
      <ConSesion>
        <MiCuenta alVolver={() => void ir({ to: '/' })} />
      </ConSesion>
    );
  },
});

const puerta = createRoute({
  getParentRoute: () => raiz,
  path: '/',
  component: Puerta,
});

/* ── §P · El panel del club ───────────────────────────────────────────────── */

/**
 * El marco: cabecera, menú y la marca del club. Todo §P cuelga de aquí.
 *
 * Es una ruta **sin camino** (`id` en vez de `path`) porque no añade un segmento a
 * la URL: `/alumnos` no es `/panel/alumnos`. Lo único que aporta es el marco y la
 * comprobación de sesión, y eso no tiene por qué verse en la barra de direcciones.
 */
/**
 * Quien puede estar dentro del panel.
 *
 * ## La puerta no basta, y eso era el agujero
 *
 * `Puerta` comprueba `can_open` y manda a cada cuenta a su aplicación, pero **solo
 * mira quien pasa por `/`**. Cualquier ruta del panel montaba el marco con tener
 * sesión, así que una cuenta de entrenador o de familia que abriera
 * `/grados/temario` —un enlace guardado, un enlace compartido, la barra del
 * navegador— pintaba el panel entero.
 *
 * No es una fuga: el servidor comprueba cada ruta y responde `403` igual, que es
 * exactamente lo que se veía —pantallas a medias y avisos de «sin permiso» encima de
 * todo—. Es lo que `BFF_BO.md` §5.1 dice que no pase: «**Sin `"panel"`, el panel no
 * es para esa persona**». Y es lo que la propia guía llama una aplicación que parece
 * rota.
 *
 * Se devuelve a la puerta y no se repite el mensaje aquí: ella ya sabe explicar el
 * caso de cada cuenta —maestro, familia, o sin club todavía— y trae el botón de
 * salir, que sin él deja a la persona atrapada con una sesión válida y ninguna
 * pantalla. No hay bucle posible: la puerta solo redirige **hacia** el panel cuando
 * `can_open` lo incluye.
 *
 * Se vio el 18-09-2026, entrando por URL con una cuenta de entrenadora.
 */
function SoloQuienAbreElPanel({ children }: { children: ReactNode }) {
  const { estado } = useSesion();

  if (estado.fase !== 'dentro') return <>{children}</>;
  if (estado.yo.can_open.includes('panel')) return <>{children}</>;

  return <Navigate to="/" replace />;
}

const panel = createRoute({
  getParentRoute: () => raiz,
  id: 'panel',
  /*
   * `ConSesion` envuelve el marco y no al revés: sin él, abrir `/cobros/recibos` en
   * una pestaña nueva monta el marco antes de que haya sesión y `useYo()` lanza.
   * Lo garantizaba `main.tsx` cuando era la única entrada; con rutas hay una por
   * URL.
   *
   * Y dentro de `ConSesion` va el guardián de `can_open`: antes de él no hay `yo`
   * que consultar.
   */
  component: function PanelConSesion() {
    if (aplicacionDeEsteDominio() === 'backoffice') {
      return <IrASuDominio aplicacion="panel" />;
    }
    return (
      <ConSesion>
        <SoloQuienAbreElPanel>
          <MarcoDelPanel />
        </SoloQuienAbreElPanel>
      </ConSesion>
    );
  },
});

/*
 * Inicio · 13-10-2026. Lo pendiente, el club en cuatro cifras y los accesos rápidos.
 * Es a donde lleva la puerta: lo primero que se ve al entrar es lo que hay que hacer.
 */
const inicio = createRoute({
  getParentRoute: () => panel,
  path: '/inicio',
  component: Inicio,
});

/*
 * «Alumnos» pasó a ser un marco con pestañas el 19-09-2026, al entrar §P.12.
 *
 * La lista se queda en `/alumnos` **a secas** —es la ruta índice, no `/alumnos/lista`—
 * porque esa dirección está en enlaces compartidos y es a la que vuelve media
 * aplicación al cerrar una ficha. Y `/alumnos/$alumnoId` sigue colgando del panel y
 * no de este marco: una ficha abierta es una pantalla entera, sin pestañas encima.
 *
 * `/alumnos/pruebas` y `/alumnos/$alumnoId` conviven porque el enrutador prefiere el
 * segmento literal al parámetro. Comprobado en el navegador, no deducido.
 */
const alumnos = createRoute({
  getParentRoute: () => panel,
  path: '/alumnos',
  component: MarcoDeAlumnos,
});

const listaDeAlumnos = createRoute({
  getParentRoute: () => alumnos,
  path: '/',
  validateSearch: conNuevo,
  component: function PantallaDeAlumnos() {
    const { club, puedeEscribir, suspendido } = useContextoDelClub();
    const { nuevo } = listaDeAlumnos.useSearch();
    const ir = useNavigate();
    useEffect(() => {
      if (nuevo) void ir({ to: '/alumnos', search: {}, replace: true });
    }, [nuevo, ir]);
    return (
      <ListaDeAlumnos
        club={club}
        puedeEscribir={puedeEscribir}
        suspendido={suspendido}
        empezarDandoDeAlta={nuevo === 1 && puedeEscribir}
        alAbrir={(id) =>
          void ir({ to: '/alumnos/$alumnoId', params: { alumnoId: id } })
        }
      />
    );
  },
});

const clasesDePrueba = createRoute({
  getParentRoute: () => alumnos,
  path: 'pruebas',
  component: function PantallaDeClasesDePrueba() {
    const { club } = useContextoDelClub();
    return <ClasesDePrueba club={club} />;
  },
});

const fichaDeAlumno = createRoute({
  getParentRoute: () => panel,
  path: '/alumnos/$alumnoId',
  component: function PantallaDeFichaDeAlumno() {
    const { club, rol, suspendido, recordarTutor } = useContextoDelClub();
    const { alumnoId } = useParams({ from: '/panel/alumnos/$alumnoId' });
    const ir = useNavigate();
    return (
      <FichaDeAlumno
        club={club}
        alumnoId={alumnoId}
        rol={rol}
        suspendido={suspendido}
        alCerrar={() => void ir({ to: '/alumnos' })}
        alVerSuAsistencia={() =>
          void ir({
            to: '/calendario/asistencia',
            search: { alumno: alumnoId },
          })
        }
        alAbrirPagador={(id) =>
          void ir({ to: '/pagadores/$pagadorId', params: { pagadorId: id } })
        }
        alVerElChat={() => void ir({ to: '/avisos/chat' })}
        /*
         * Los datos del tutor **no viajan por la URL**: son de una persona, y la
         * URL acaba en un historial, en una captura y en un chat. Se recuerdan en
         * el contexto del club y se pierden al recargar, que es lo correcto.
         */
        alCrearPagador={(desde) => {
          recordarTutor(desde);
          void ir({ to: '/pagadores' });
        }}
      />
    );
  },
});

const grupos = createRoute({
  getParentRoute: () => panel,
  path: '/grupos',
  validateSearch: conNuevoYVolver,
  component: function PantallaDeGrupos() {
    const { club, puedeEscribir, suspendido } = useContextoDelClub();
    const ir = useNavigate();
    const { empezarCreando } = useNuevoYVolver(grupos.useSearch(), '/grupos');
    return (
      <ListaDeGrupos
        club={club}
        puedeEscribir={puedeEscribir}
        suspendido={suspendido}
        empezarCreando={empezarCreando && puedeEscribir && !suspendido}
        alAbrir={(id) =>
          void ir({ to: '/grupos/$grupoId', params: { grupoId: id! } })
        }
      />
    );
  },
});

const fichaDeGrupo = createRoute({
  getParentRoute: () => panel,
  path: '/grupos/$grupoId',
  component: function PantallaDeFichaDeGrupo() {
    const { club, rol, suspendido } = useContextoDelClub();
    const { grupoId } = useParams({ from: '/panel/grupos/$grupoId' });
    const ir = useNavigate();
    return (
      <FichaDeGrupo
        club={club}
        grupoId={grupoId}
        rol={rol}
        suspendido={suspendido}
        alCerrar={() => void ir({ to: '/grupos' })}
        alAbrirAlumno={(id) =>
          void ir({ to: '/alumnos/$alumnoId', params: { alumnoId: id } })
        }
      />
    );
  },
});

const calendario = createRoute({
  getParentRoute: () => panel,
  path: '/calendario',
  component: MarcoDelCalendario,
});

const calendarioIndice = createRoute({
  getParentRoute: () => calendario,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/calendario/clases' });
  },
});

const clases = createRoute({
  getParentRoute: () => calendario,
  path: 'clases',
  component: function PantallaDeCalendario() {
    const { club, rol, suspendido, permisos } = useContextoDelClub();
    const ir = useNavigate();
    return (
      <Calendario
        club={club}
        puedeGenerar={permisos.generarCalendario(rol)}
        puedeEditarClase={permisos.moverUnaClase(rol)}
        suspendido={suspendido}
        alAbrirGrupo={(id) =>
          void ir({ to: '/grupos/$grupoId', params: { grupoId: id! } })
        }
      />
    );
  },
});

const asistenciaDelCalendario = createRoute({
  getParentRoute: () => calendario,
  path: 'asistencia',
  /*
   * `alumno` en la URL y opcional: se llega aquí desde la ficha de un alumno con su
   * asistencia ya filtrada, y también a secas desde la pestaña. Es un identificador
   * de alumno en la barra del navegador, igual que `/alumnos/$alumnoId`, así que no
   * añade nada que no estuviera ya.
   */
  validateSearch: (b: Record<string, unknown>): { alumno?: string } =>
    typeof b['alumno'] === 'string' ? { alumno: b['alumno'] } : {},
  component: function PantallaDeAsistencia() {
    const { club } = useContextoDelClub();
    const { alumno } = asistenciaDelCalendario.useSearch();
    const ir = useNavigate();
    return (
      <AsistenciaDelCalendario
        club={club}
        {...(alumno ? { alumnoInicial: alumno } : {})}
        alAbrirAlumno={(id) =>
          void ir({ to: '/alumnos/$alumnoId', params: { alumnoId: id } })
        }
      />
    );
  },
});

const ausencias = createRoute({
  getParentRoute: () => calendario,
  path: 'ausencias',
  component: function PantallaDeAusencias() {
    const { club } = useContextoDelClub();
    const ir = useNavigate();
    return (
      <AvisosDeAusencia
        club={club}
        alAbrirAlumno={(id) =>
          void ir({ to: '/alumnos/$alumnoId', params: { alumnoId: id } })
        }
      />
    );
  },
});

const avisos = createRoute({
  getParentRoute: () => panel,
  path: '/avisos',
  component: MarcoDeComunicacion,
});

const avisosIndice = createRoute({
  getParentRoute: () => avisos,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/avisos/publicados' });
  },
});

const avisosPublicados = createRoute({
  getParentRoute: () => avisos,
  path: 'publicados',
  validateSearch: conNuevo,
  component: function PantallaDeAvisos() {
    const { club, rol, suspendido, permisos } = useContextoDelClub();
    const { nuevo } = avisosPublicados.useSearch();
    const ir = useNavigate();
    useEffect(() => {
      if (nuevo) {
        void ir({ to: '/avisos/publicados', search: {}, replace: true });
      }
    }, [nuevo, ir]);
    return (
      <Avisos
        club={club}
        empezarEscribiendo={nuevo === 1 && permisos.publicarAvisos(rol)}
        puedeEscribir={permisos.publicarAvisos(rol)}
        puedeCorregir={permisos.corregirAvisos(rol)}
        suspendido={suspendido}
        alVerAcuses={(id) =>
          void ir({
            to: '/avisos/publicados/$avisoId/acuses',
            params: { avisoId: id },
          })
        }
      />
    );
  },
});

const chat = createRoute({
  getParentRoute: () => avisos,
  path: 'chat',
  component: function PantallaDeChat() {
    const { club, rol, suspendido, permisos } = useContextoDelClub();
    const yo = useYo();
    return (
      <Chat
        club={club}
        /* Mi `user_id`: es lo que distingue mis mensajes de los de la familia. */
        yoSoy={yo.user.id}
        puedeEscribir={permisos.publicarAvisos(rol) && !suspendido}
      />
    );
  },
});

const muro = createRoute({
  getParentRoute: () => avisos,
  path: 'muro',
  component: function PantallaDelMuro() {
    const { club, rol, suspendido, permisos } = useContextoDelClub();
    const ir = useNavigate();
    return (
      <Muro
        club={club}
        /* Retirar del muro es `own` y `adm`, igual que retirar un aviso. */
        puedeRetirar={permisos.corregirAvisos(rol) && !suspendido}
        alAbrirAlumno={(id) =>
          void ir({ to: '/alumnos/$alumnoId', params: { alumnoId: id } })
        }
      />
    );
  },
});

const silencio = createRoute({
  getParentRoute: () => avisos,
  path: 'silencio',
  component: function PantallaDeSilencio() {
    const { club, rol, suspendido, permisos } = useContextoDelClub();

    /* Las dos rutas del horario son `own` y `adm`. */
    if (!permisos.corregirAvisos(rol))
      return <Navigate to="/avisos/publicados" replace />;

    return (
      <HorarioDeSilencio
        club={club}
        puedeEditar={permisos.corregirAvisos(rol) && !suspendido}
      />
    );
  },
});

const acusesDeAviso = createRoute({
  getParentRoute: () => avisos,
  path: 'publicados/$avisoId/acuses',
  component: function PantallaDeAcuses() {
    const { club } = useContextoDelClub();
    const { avisoId } = useParams({
      from: '/panel/avisos/publicados/$avisoId/acuses',
    });
    const ir = useNavigate();
    return (
      <AcusesDeAviso
        club={club}
        avisoId={avisoId}
        alCerrar={() => void ir({ to: '/avisos/publicados' })}
      />
    );
  },
});

const grados = createRoute({
  getParentRoute: () => panel,
  path: '/grados',
  component: MarcoDeGrados,
});

const gradosIndice = createRoute({
  getParentRoute: () => grados,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/grados/sistemas' });
  },
});

const sistemasDeGrados = createRoute({
  getParentRoute: () => grados,
  path: 'sistemas',
  component: function PantallaDeGrados() {
    const { club, rol, suspendido, permisos } = useContextoDelClub();
    const ir = useNavigate();
    return (
      <SistemasDeGrados
        club={club}
        puedeEditar={permisos.editarLaEscalera(rol) && !suspendido}
        alEditarEscalera={(id) =>
          void ir({
            to: '/grados/sistemas/$schemeId',
            params: { schemeId: id },
          })
        }
      />
    );
  },
});

const examenes = createRoute({
  getParentRoute: () => grados,
  path: 'examenes',
  component: function PantallaDeExamenes() {
    const { club, rol, suspendido } = useContextoDelClub();
    const ir = useNavigate();
    return (
      <Examenes
        club={club}
        /* Convocar es de quien puede otorgar grados: el entrenador incluido. */
        puedeEditar={puedeOtorgarGrados(rol) && !suspendido}
        alAbrirExamen={(id) =>
          void ir({
            to: '/grados/examenes/$examenId',
            params: { examenId: id },
          })
        }
      />
    );
  },
});

const fichaDeExamen = createRoute({
  getParentRoute: () => grados,
  path: 'examenes/$examenId',
  component: function PantallaDeFichaDeExamen() {
    const { club, rol, suspendido } = useContextoDelClub();
    const { examenId } = useParams({
      from: '/panel/grados/examenes/$examenId',
    });
    const ir = useNavigate();
    return (
      <FichaDeExamen
        club={club}
        examenId={examenId}
        puedeEditar={puedeOtorgarGrados(rol) && !suspendido}
        alCerrar={() => void ir({ to: '/grados/examenes' })}
        alAbrirAlumno={(id) =>
          void ir({ to: '/alumnos/$alumnoId', params: { alumnoId: id } })
        }
      />
    );
  },
});

const temario = createRoute({
  getParentRoute: () => grados,
  path: 'temario',
  component: function PantallaDeTemario() {
    const { club, rol, suspendido } = useContextoDelClub();
    return (
      <Temario
        club={club}
        /*
         * El entrenador escribe el temario y corrige lo que ve mal; quitar una
         * técnica es del club. Las dos banderas van a la misma pantalla en vez de
         * esconderle la pestaña: el temario es justo lo que más consulta él.
         */
        puedeEditar={puedeEditarElTemario(rol) && !suspendido}
        puedeQuitar={puedeQuitarDelTemario(rol) && !suspendido}
      />
    );
  },
});

const escalera = createRoute({
  getParentRoute: () => grados,
  path: 'sistemas/$schemeId',
  component: function PantallaDeEscalera() {
    const { club } = useContextoDelClub();
    const { schemeId } = useParams({
      from: '/panel/grados/sistemas/$schemeId',
    });
    const ir = useNavigate();
    return (
      <EscaleraDeGrados
        club={club}
        schemeId={schemeId}
        alCerrar={() => void ir({ to: '/grados' })}
      />
    );
  },
});

/* ── §P.3 + §P.4 · Cobros, con sus pestañas ───────────────────────────────── */

/**
 * «Cobros» es un marco con pestañas, no una pantalla.
 *
 * Y `/cobros` a secas lleva a los recibos: es lo que se mira todos los días, y la
 * remesa —la operación peligrosa— no se pone donde se cae el dedo.
 */
const cobros = createRoute({
  getParentRoute: () => panel,
  path: '/cobros',
  component: MarcoDeCobros,
});

const cobrosIndice = createRoute({
  getParentRoute: () => cobros,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/cobros/recibos' });
  },
});

const recibos = createRoute({
  getParentRoute: () => cobros,
  path: 'recibos',
  /*
   * `periodo` **opcional en el tipo**, no «de tipo `string | undefined`»: con lo
   * segundo TanStack lo exige en cada navegación a esta ruta, y a los recibos se
   * llega casi siempre sin filtro de mes.
   *
   * `deudas=1`: con «Solo lo que se debe» ya marcado. Es como llega «Anotar un
   * cobro» de Inicio: quien viene a anotar un cobro busca lo que está por cobrar.
   */
  validateSearch: (
    b: Record<string, unknown>
  ): { periodo?: string; deudas?: 1 } => ({
    ...(typeof b['periodo'] === 'string' ? { periodo: b['periodo'] } : {}),
    ...(b['deudas'] === 1 || b['deudas'] === '1' ? { deudas: 1 as const } : {}),
  }),
  component: function PantallaDeRecibos() {
    const { club, puedeEscribir, suspendido } = useContextoDelClub();
    const { periodo, deudas } = recibos.useSearch();
    const ir = useNavigate();
    return (
      <ListaDeRecibos
        club={club}
        puedeCobrar={puedeEscribir && !suspendido}
        {...(periodo ? { periodoInicial: periodo } : {})}
        soloDeudasAlEmpezar={deudas === 1}
        alAbrir={(id) =>
          void ir({
            to: '/cobros/recibos/$reciboId',
            params: { reciboId: id },
          })
        }
      />
    );
  },
});

const fichaDeRecibo = createRoute({
  getParentRoute: () => cobros,
  path: 'recibos/$reciboId',
  component: function PantallaDeFichaDeRecibo() {
    const { club, puedeEscribir, suspendido } = useContextoDelClub();
    const { reciboId } = useParams({
      from: '/panel/cobros/recibos/$reciboId',
    });
    const ir = useNavigate();
    return (
      <FichaDeRecibo
        club={club}
        reciboId={reciboId}
        puedeCobrar={puedeEscribir && !suspendido}
        alCerrar={() => void ir({ to: '/cobros/recibos' })}
        alAbrirPagador={(id) =>
          void ir({ to: '/pagadores/$pagadorId', params: { pagadorId: id } })
        }
      />
    );
  },
});

const morosos = createRoute({
  getParentRoute: () => cobros,
  path: 'morosos',
  component: function PantallaDeMorosos() {
    const { club } = useContextoDelClub();
    const ir = useNavigate();
    return (
      <Morosos
        club={club}
        alAbrirPagador={(id) =>
          void ir({ to: '/pagadores/$pagadorId', params: { pagadorId: id } })
        }
      />
    );
  },
});

const loCobrado = createRoute({
  getParentRoute: () => cobros,
  path: 'cobrado',
  component: function PantallaDeLoCobrado() {
    const { club } = useContextoDelClub();
    const ir = useNavigate();
    return (
      <LoCobrado
        club={club}
        alAbrirRecibo={(id) =>
          void ir({ to: '/cobros/recibos/$reciboId', params: { reciboId: id } })
        }
      />
    );
  },
});

const remesa = createRoute({
  getParentRoute: () => cobros,
  path: 'remesa',
  component: function PantallaDeRemesa() {
    const { club, puedeEscribir, suspendido } = useContextoDelClub();
    const ir = useNavigate();
    return (
      <Remesa
        club={club}
        puedeLanzar={puedeEscribir && !suspendido}
        alVerRecibosDe={(periodo) =>
          void ir({ to: '/cobros/recibos', search: { periodo } })
        }
      />
    );
  },
});

const tarifas = createRoute({
  getParentRoute: () => cobros,
  path: 'tarifas',
  validateSearch: conNuevoYVolver,
  component: function PantallaDeTarifas() {
    const { club, puedeEscribir, suspendido } = useContextoDelClub();
    const ir = useNavigate();
    const { empezarCreando, alCrear } = useNuevoYVolver(
      tarifas.useSearch(),
      '/cobros/tarifas'
    );
    return (
      <Tarifas
        club={club}
        empezarCreando={empezarCreando && puedeEscribir && !suspendido}
        alCrear={alCrear}
        puedeEditar={puedeEscribir && !suspendido}
        alVerCuotasDe={() => void ir({ to: '/alumnos' })}
      />
    );
  },
});

const descuentos = createRoute({
  getParentRoute: () => cobros,
  path: 'descuentos',
  component: function PantallaDeDescuentos() {
    const { club, puedeEscribir, suspendido } = useContextoDelClub();
    const ir = useNavigate();
    return (
      <CatalogoDeDescuentos
        club={club}
        puedeEditar={puedeEscribir && !suspendido}
        alAbrirAlumno={(id) =>
          void ir({ to: '/alumnos/$alumnoId', params: { alumnoId: id } })
        }
      />
    );
  },
});

const devoluciones = createRoute({
  getParentRoute: () => cobros,
  path: 'devoluciones',
  component: function PantallaDeDevoluciones() {
    const { club } = useContextoDelClub();
    const ir = useNavigate();
    return (
      <Devoluciones
        club={club}
        alAbrirRecibo={(id) =>
          void ir({ to: '/cobros/recibos/$reciboId', params: { reciboId: id } })
        }
      />
    );
  },
});

const pagadores = createRoute({
  getParentRoute: () => panel,
  path: '/pagadores',
  component: function PantallaDePagadores() {
    const { club, puedeEscribir, suspendido, pagadorDesdeTutor, olvidarTutor } =
      useContextoDelClub();
    const ir = useNavigate();
    return (
      <ListaDePagadores
        club={club}
        puedeEscribir={puedeEscribir}
        suspendido={suspendido}
        {...(pagadorDesdeTutor ? { desdeTutor: pagadorDesdeTutor } : {})}
        alTerminarConTutor={olvidarTutor}
        alAbrir={(id) =>
          void ir({ to: '/pagadores/$pagadorId', params: { pagadorId: id! } })
        }
      />
    );
  },
});

const fichaDePagador = createRoute({
  getParentRoute: () => panel,
  path: '/pagadores/$pagadorId',
  component: function PantallaDeFichaDePagador() {
    const { club, puedeEscribir, suspendido } = useContextoDelClub();
    const { pagadorId } = useParams({ from: '/panel/pagadores/$pagadorId' });
    const ir = useNavigate();
    return (
      <FichaDePagador
        club={club}
        pagadorId={pagadorId}
        puedeEscribir={puedeEscribir}
        suspendido={suspendido}
        alCerrar={() => void ir({ to: '/pagadores' })}
        alAbrirAlumno={(id) =>
          void ir({ to: '/alumnos/$alumnoId', params: { alumnoId: id } })
        }
      />
    );
  },
});

/* ── §A · El back office ──────────────────────────────────────────────────── */

/**
 * Quien puede estar dentro del back office. El gemelo de `SoloQuienAbreElPanel`.
 *
 * Faltaba, y se vio el 12-10-2026 con el primer club de verdad: el dueño aceptó su
 * invitación y acabó en `/admin/clubes`, con el back office entero y «Sin permiso»
 * encima. El servidor hacía bien en negárselo; lo que sobraba era el marco. Se
 * devuelve a la puerta, que lo manda a su panel.
 */
function SoloQuienAbreElBackOffice({ children }: { children: ReactNode }) {
  const { estado } = useSesion();

  if (estado.fase !== 'dentro') return <>{children}</>;
  if (estado.yo.can_open.includes('backoffice')) return <>{children}</>;

  return <Navigate to="/" replace />;
}

const admin = createRoute({
  getParentRoute: () => raiz,
  id: 'admin',
  component: function BackOfficeConSesion() {
    if (aplicacionDeEsteDominio() === 'panel') {
      return <IrASuDominio aplicacion="backoffice" />;
    }
    return (
      <ConSesion>
        <SoloQuienAbreElBackOffice>
          <MarcoDelBackOffice />
        </SoloQuienAbreElBackOffice>
      </ConSesion>
    );
  },
});

const adminRaiz = createRoute({
  getParentRoute: () => admin,
  path: '/admin',
  beforeLoad: () => {
    throw redirect({ to: '/admin/hoy' });
  },
});

/** §A.8 · Lo pendiente de hoy, la pantalla de entrada (13-10-2026). */
const adminHoy = createRoute({
  getParentRoute: () => admin,
  path: '/admin/hoy',
  component: Hoy,
});

const adminClubes = createRoute({
  getParentRoute: () => admin,
  path: '/admin/clubes',
  component: function PantallaDeClubes() {
    const ir = useNavigate();
    return (
      <Clubes
        alAbrir={(id) =>
          void ir({ to: '/admin/clubes/$clubId', params: { clubId: id } })
        }
      />
    );
  },
});

const adminFichaDeClub = createRoute({
  getParentRoute: () => admin,
  path: '/admin/clubes/$clubId',
  component: function PantallaDeFichaDeClub() {
    const { clubId } = useParams({ from: '/admin/admin/clubes/$clubId' });
    const { pedirSoporteDe } = useContextoDelBackOffice();
    const ir = useNavigate();
    return (
      <FichaDeClub
        clubId={clubId}
        alCerrar={() => void ir({ to: '/admin/clubes' })}
        alAbrirPersona={(id) =>
          void ir({
            to: '/admin/soporte/personas/$personaId',
            params: { personaId: id },
          })
        }
        alEntrarEnSoporte={(id) => {
          pedirSoporteDe(id);
          void ir({ to: '/admin/soporte/personas' });
        }}
      />
    );
  },
});

/* ── §A.2 · «Dinero», con sus pestañas ───────────────────────────────────── */

/**
 * Tres vistas del dinero **de Kiap**: quién no nos ha pagado, qué vendemos y cómo va
 * el MRR. Eran una sección suelta llamada «Cobros»; el porqué de juntarlas está en
 * `backoffice/secciones.ts`.
 */
const adminDinero = createRoute({
  getParentRoute: () => admin,
  path: '/admin/dinero',
  component: function MarcoDeDinero() {
    return (
      <MarcoDeSeccion
        seccion="dinero"
        etiqueta="Dinero"
        vistas={VISTAS_DE_DINERO}
      />
    );
  },
});

const adminDineroIndice = createRoute({
  getParentRoute: () => adminDinero,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/admin/dinero/avisos' });
  },
});

const adminAvisosDePlanes = createRoute({
  getParentRoute: () => adminDinero,
  path: 'avisos',
  component: function PantallaDeAvisosDePlanes() {
    const ir = useNavigate();
    return (
      <AvisosDePlanes
        alAbrirClub={(id) =>
          void ir({ to: '/admin/clubes/$clubId', params: { clubId: id } })
        }
      />
    );
  },
});

const adminImpagados = createRoute({
  getParentRoute: () => adminDinero,
  path: 'impagados',
  component: function PantallaDeCobrosDePlataforma() {
    const ir = useNavigate();
    return (
      <CobrosDePlataforma
        alAbrirClub={(id) =>
          void ir({ to: '/admin/clubes/$clubId', params: { clubId: id } })
        }
      />
    );
  },
});

const adminPlanes = createRoute({
  getParentRoute: () => adminDinero,
  path: 'planes',
  component: Planes,
});

const adminMetricas = createRoute({
  getParentRoute: () => adminDinero,
  path: 'metricas',
  component: Metricas,
});

/* ── §A.3 · «Soporte», con sus pestañas ──────────────────────────────────── */

/**
 * Lo que se abre cuando suena el teléfono. Las personas primero: la llamada empieza
 * con «soy Manuel, del Dojang Pascual» y lo primero es encontrarlo.
 */
const adminSoporte = createRoute({
  getParentRoute: () => admin,
  path: '/admin/soporte',
  component: function MarcoDeSoporte() {
    return (
      <MarcoDeSeccion
        seccion="soporte"
        etiqueta="Soporte"
        vistas={VISTAS_DE_SOPORTE}
      />
    );
  },
});

const adminSoporteIndice = createRoute({
  getParentRoute: () => adminSoporte,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/admin/soporte/personas' });
  },
});

const adminPersonas = createRoute({
  getParentRoute: () => adminSoporte,
  path: 'personas',
  component: function PantallaDeSoporte() {
    const { clubParaSoporte, olvidarSoporte } = useContextoDelBackOffice();
    const ir = useNavigate();
    return (
      <Soporte
        {...(clubParaSoporte ? { clubParaSoporte } : {})}
        alTerminarConElClub={olvidarSoporte}
        alAbrirPersona={(id) =>
          id === null
            ? void ir({ to: '/admin/soporte/personas' })
            : void ir({
                to: '/admin/soporte/personas/$personaId',
                params: { personaId: id },
              })
        }
      />
    );
  },
});

const adminPersona = createRoute({
  getParentRoute: () => adminSoporte,
  path: 'personas/$personaId',
  component: function PantallaDePersona() {
    const { personaId } = useParams({
      from: '/admin/admin/soporte/personas/$personaId',
    });
    const ir = useNavigate();
    return (
      <Soporte
        personaAbierta={personaId}
        alTerminarConElClub={() => undefined}
        alAbrirPersona={(id) =>
          id === null
            ? void ir({ to: '/admin/soporte/personas' })
            : void ir({
                to: '/admin/soporte/personas/$personaId',
                params: { personaId: id },
              })
        }
      />
    );
  },
});

const adminAvisos = createRoute({
  getParentRoute: () => adminSoporte,
  path: 'avisos',
  component: TrazasDeAvisos,
});

const adminSalud = createRoute({
  getParentRoute: () => adminSoporte,
  path: 'salud',
  component: function PantallaDeSalud() {
    const ir = useNavigate();
    return (
      <SaludDeLosClubes
        alAbrirClub={(id) =>
          void ir({ to: '/admin/clubes/$clubId', params: { clubId: id } })
        }
      />
    );
  },
});

const adminAccesos = createRoute({
  getParentRoute: () => adminSoporte,
  path: 'accesos',
  component: Accesos,
});

/* ── §A.5 · «Operación», con sus pestañas ────────────────────────────────── */

/**
 * Qué se rompió anoche mientras nadie miraba. Los procesos primero: son las 2:30 de
 * la mañana de doscientos clubes.
 */
const adminOperacion = createRoute({
  getParentRoute: () => admin,
  path: '/admin/operacion',
  component: function MarcoDeOperacion() {
    return (
      <MarcoDeSeccion
        seccion="operacion"
        etiqueta="Operación"
        vistas={VISTAS_DE_OPERACION}
      />
    );
  },
});

const adminOperacionIndice = createRoute({
  getParentRoute: () => adminOperacion,
  path: '/',
  beforeLoad: () => {
    // A «Estado», que pasó a ser la primera: es la que se abre con prisa.
    throw redirect({ to: '/admin/operacion/estado' });
  },
});

const adminProcesos = createRoute({
  getParentRoute: () => adminOperacion,
  path: 'procesos',
  component: Procesos,
});

const adminWebhooks = createRoute({
  getParentRoute: () => adminOperacion,
  path: 'webhooks',
  component: Webhooks,
});

const adminDatos = createRoute({
  getParentRoute: () => adminOperacion,
  path: 'datos',
  component: SolicitudesRgpd,
});

const adminEquipo = createRoute({
  getParentRoute: () => adminOperacion,
  path: 'equipo',
  component: EquipoDeKiap,
});

/* ── §A.4 · «Comercial» ──────────────────────────────────────────────────── */

/** Sin pestañas: el embudo y la lista son la misma pantalla y se leen juntos. */
const adminComercial = createRoute({
  getParentRoute: () => admin,
  path: '/admin/comercial',
  component: function PantallaDeComercial() {
    const ir = useNavigate();
    return (
      <Comercial
        alAbrirClub={(id) =>
          void ir({ to: '/admin/clubes/$clubId', params: { clubId: id } })
        }
      />
    );
  },
});

/* ── §A.6 · «Catálogos», con sus pestañas ────────────────────────────────── */

/**
 * Lo que comparten los doscientos clubes. Se toca dos veces al año y **cada cambio
 * los afecta a todos**, que es lo que dicen las tres pantallas.
 */
const adminCatalogos = createRoute({
  getParentRoute: () => admin,
  path: '/admin/catalogos',
  component: function MarcoDeCatalogos() {
    return (
      <MarcoDeSeccion
        seccion="catalogos"
        etiqueta="Catálogos"
        vistas={VISTAS_DE_CATALOGOS}
      />
    );
  },
});

const adminCatalogosIndice = createRoute({
  getParentRoute: () => adminCatalogos,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/admin/catalogos/disciplinas' });
  },
});

const adminDisciplinas = createRoute({
  getParentRoute: () => adminCatalogos,
  path: 'disciplinas',
  component: CatalogoDeDisciplinas,
});

const adminPlantillas = createRoute({
  getParentRoute: () => adminCatalogos,
  path: 'grados',
  component: CatalogoDeGrados,
});

const adminConsentimientos = createRoute({
  getParentRoute: () => adminCatalogos,
  path: 'consentimientos',
  component: CatalogoDeConsentimientos,
});

/* ── §A.7 · «Directorio», con sus pestañas ───────────────────────────────── */

/**
 * El censo público. Las reclamaciones primero: es lo único con alguien esperando al
 * otro lado, y el contrato mide esa espera en horas por algo.
 */
const adminDirectorio = createRoute({
  getParentRoute: () => admin,
  path: '/admin/directorio',
  component: function MarcoDeDirectorio() {
    return (
      <MarcoDeSeccion
        seccion="directorio"
        etiqueta="Directorio"
        vistas={VISTAS_DE_DIRECTORIO}
      />
    );
  },
});

const adminDirectorioIndice = createRoute({
  getParentRoute: () => adminDirectorio,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/admin/directorio/reclamaciones' });
  },
});

const adminReclamaciones = createRoute({
  getParentRoute: () => adminDirectorio,
  path: 'reclamaciones',
  component: function PantallaDeReclamaciones() {
    const ir = useNavigate();
    return (
      <Reclamaciones
        alAbrirClub={(id) =>
          void ir({ to: '/admin/clubes/$clubId', params: { clubId: id } })
        }
      />
    );
  },
});

const adminBajasDelDirectorio = createRoute({
  getParentRoute: () => adminDirectorio,
  path: 'bajas',
  component: function PantallaDeBajasDelDirectorio() {
    const ir = useNavigate();
    return (
      <BajasDelDirectorio
        alAbrirClub={(id) =>
          void ir({ to: '/admin/clubes/$clubId', params: { clubId: id } })
        }
      />
    );
  },
});

const adminImportar = createRoute({
  getParentRoute: () => adminDirectorio,
  path: 'importar',
  component: ImportarCenso,
});

const adminComparativas = createRoute({
  getParentRoute: () => adminDirectorio,
  path: 'comparativas',
  component: Comparativas,
});

const adminEstado = createRoute({
  getParentRoute: () => adminOperacion,
  path: 'estado',
  component: EstadoDelServicio,
});

const adminInterruptores = createRoute({
  getParentRoute: () => adminOperacion,
  path: 'interruptores',
  component: function PantallaDeInterruptores() {
    const ir = useNavigate();
    return (
      <InterruptoresGlobales
        alAbrirClub={(id) =>
          void ir({ to: '/admin/clubes/$clubId', params: { clubId: id } })
        }
      />
    );
  },
});

/* ── §P.7 · El club, con sus pestañas ─────────────────────────────────────── */

/**
 * Igual que «Cobros»: un marco con pestañas, y `/club` a secas lleva a los datos.
 *
 * Los datos primero porque es lo que se viene a mirar; «Personal» es la única
 * pestaña que a un administrativo no le sale, y una que le falta a la mitad de la
 * gente no puede ser la de entrada.
 */
const elClub = createRoute({
  getParentRoute: () => panel,
  path: '/club',
  component: MarcoDelClub,
});

const elClubIndice = createRoute({
  getParentRoute: () => elClub,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/club/datos' });
  },
});

const clubDatos = createRoute({
  getParentRoute: () => elClub,
  path: 'datos',
  component: function PantallaDeDatosDelClub() {
    const { club, rol, suspendido, plan } = useContextoDelClub();
    return (
      <>
        <VuestroPlan plan={plan} />
        <DatosDelClub
          club={club}
          puedeEditar={puedeEditarElClub(rol) && !suspendido}
        />
      </>
    );
  },
});

const clubMarca = createRoute({
  getParentRoute: () => elClub,
  path: 'marca',
  component: function PantallaDeMarca() {
    const { club, rol, suspendido } = useContextoDelClub();
    return (
      <Marca club={club} puedeEditar={puedeEditarElClub(rol) && !suspendido} />
    );
  },
});

const clubAjustes = createRoute({
  getParentRoute: () => elClub,
  path: 'ajustes',
  component: function PantallaDeAjustes() {
    const { club, rol, suspendido } = useContextoDelClub();
    return (
      <Interruptores
        club={club}
        puedeEditar={puedeEditarElClub(rol) && !suspendido}
      />
    );
  },
});

const clubSedes = createRoute({
  getParentRoute: () => elClub,
  path: 'sedes',
  validateSearch: conNuevoYVolver,
  component: function PantallaDeSedes() {
    const { club, rol, suspendido } = useContextoDelClub();
    const puedeEditar = puedeEditarElClub(rol) && !suspendido;
    const { empezarCreando, alCrear } = useNuevoYVolver(
      clubSedes.useSearch(),
      '/club/sedes'
    );
    return (
      <Sedes
        club={club}
        empezarCreando={empezarCreando && puedeEditar}
        alCrear={alCrear}
        puedeEditar={puedeEditarElClub(rol) && !suspendido}
        /* Cerrar una sede es `own` a secas: ver `permisos.ts`. */
        puedeCerrar={puedeCerrarUnaSede(rol) && !suspendido}
      />
    );
  },
});

const clubDisciplinas = createRoute({
  getParentRoute: () => elClub,
  path: 'disciplinas',
  component: function PantallaDeDisciplinas() {
    const { club, rol, suspendido } = useContextoDelClub();
    return (
      <Disciplinas
        club={club}
        puedeEditar={puedeEditarElClub(rol) && !suspendido}
      />
    );
  },
});

const clubFicha = createRoute({
  getParentRoute: () => elClub,
  path: 'ficha',
  component: function PantallaDeFichaPublica() {
    const { club, rol } = useContextoDelClub();
    /*
     * `unpublish` declara `own` a secas, y con razón: retirar la ficha cierra la
     * puerta por la que entran alumnos nuevos. Publicar y editar sí las puede el
     * administrativo, así que la pantalla entera no se le esconde — solo ese botón.
     *
     * No se comprueba `suspendido`: un club suspendido puede leerlo todo y el
     * servidor corta sus escrituras con `423`. Esconderle esta pantalla no añadiría
     * nada y le quitaría poder mirar qué enseña de sí mismo.
     */
    return <FichaPublica club={club} puedeRetirar={rol === 'owner'} />;
  },
});

const clubPersonal = createRoute({
  getParentRoute: () => elClub,
  path: 'personal',
  component: function PantallaDePersonal() {
    const { club, rol, suspendido, tiene } = useContextoDelClub();
    const yo = useYo();
    const [papeles, setPapeles] = useState<{
      usuarioId: string;
      nombre: string;
    } | null>(null);

    /*
     * El segundo guardián, y no sobra: el marco deja pasar al administrativo —las
     * otras tres pestañas son suyas— y esta no lo es. Sin esto, escribir
     * `/club/personal` en la barra le abre la pantalla que decide quién ve el
     * dinero del club.
     */
    if (!puedeGestionarPersonal(rol))
      return <Navigate to="/club/datos" replace />;

    if (papeles) {
      return (
        <DocumentosDePersonal
          club={club}
          usuarioId={papeles.usuarioId}
          nombre={papeles.nombre}
          puedeEditar={!suspendido}
          alCerrar={() => setPapeles(null)}
        />
      );
    }

    /*
     * Los papeles se abren **como estado de esta ruta y no como ruta propia**, al
     * contrario que casi todo lo demás del panel. Es a propósito: un enlace a
     * «los papeles de Marta» lleva el `user_id` de una persona en la URL, y eso
     * acaba en el historial del navegador y en el registro del servidor. Es la
     * misma razón por la que el pagador desde un tutor viaja en memoria (§P.1).
     */
    return (
      <Personal
        club={club}
        yoSoy={yo.user.id}
        alVerPapeles={(usuarioId, nombre) => setPapeles({ usuarioId, nombre })}
        conRoles={tiene('roles')}
        conSedes={tiene('multisede')}
      />
    );
  },
});

/* ── §P.8 · Cumplimiento, con sus pestañas ────────────────────────────────── */

/**
 * `/cumplimiento` a secas lleva a los consentimientos: es la lista que se persigue,
 * no el contrato, que se firma una vez y no se vuelve a mirar.
 */
const cumplimiento = createRoute({
  getParentRoute: () => panel,
  path: '/cumplimiento',
  component: MarcoDeCumplimiento,
});

const cumplimientoIndice = createRoute({
  getParentRoute: () => cumplimiento,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/cumplimiento/consentimientos' });
  },
});

const consentimientos = createRoute({
  getParentRoute: () => cumplimiento,
  path: 'consentimientos',
  component: function PantallaDeConsentimientos() {
    const { club } = useContextoDelClub();
    const ir = useNavigate();
    return (
      <Consentimientos
        club={club}
        alAbrirAlumno={(id) =>
          void ir({ to: '/alumnos/$alumnoId', params: { alumnoId: id } })
        }
        /* La única acción que cabe desde ahí: pedírselo, y eso es §P.6. */
        alAvisar={() => void ir({ to: '/avisos/publicados' })}
      />
    );
  },
});

const solicitudesDeDatos = createRoute({
  getParentRoute: () => cumplimiento,
  path: 'solicitudes',
  component: function PantallaDeSolicitudes() {
    const { club, suspendido } = useContextoDelClub();
    const ir = useNavigate();
    return (
      <SolicitudesDeDatos
        club={club}
        /* `own` y `adm`: el marco ya filtró, y resolver es la misma pareja. */
        puedeResolver={!suspendido}
        alAbrirAlumno={(id) =>
          void ir({ to: '/alumnos/$alumnoId', params: { alumnoId: id } })
        }
      />
    );
  },
});

const exportarTodo = createRoute({
  getParentRoute: () => cumplimiento,
  path: 'exportar',
  component: function PantallaDeExportacion() {
    const { club, rol } = useContextoDelClub();

    /* `GET /exports/full` es `own` a secas: el marco deja pasar al administrativo. */
    if (!puedeFirmarElContrato(rol))
      return <Navigate to="/cumplimiento/consentimientos" replace />;

    return <Exportacion club={club} />;
  },
});

const accesos = createRoute({
  getParentRoute: () => cumplimiento,
  path: 'accesos',
  component: function PantallaDeAccesos() {
    const { club } = useContextoDelClub();
    return <RegistroDeAccesos club={club} />;
  },
});

const soporte = createRoute({
  getParentRoute: () => cumplimiento,
  path: 'soporte',
  component: function PantallaDeModoSoporte() {
    const { club } = useContextoDelClub();
    return <ModoSoporte club={club} />;
  },
});

const contrato = createRoute({
  getParentRoute: () => cumplimiento,
  path: 'contrato',
  component: function PantallaDelContrato() {
    const { club, rol } = useContextoDelClub();
    const yo = useYo();

    /* Las dos rutas del DPA son `own`, y el marco deja pasar al administrativo. */
    if (!puedeFirmarElContrato(rol))
      return <Navigate to="/cumplimiento/consentimientos" replace />;

    return (
      <ContratoDeEncargo
        club={club}
        /*
         * Firma una persona con su nombre, no «el club». Se propone el de quien está
         * mirando y se puede cambiar: puede estar firmando de parte del presidente.
         */
        nombreSugerido={[yo.user.first_name, yo.user.last_name]
          .filter(Boolean)
          .join(' ')}
      />
    );
  },
});

/* ── §P.9 · Informes, con sus pestañas ────────────────────────────────────── */

/**
 * `/informes` a secas lleva a la asistencia, y no a los ingresos: es la única que ve
 * el entrenador, y con los ingresos por defecto la sección le abriría en una
 * pantalla que no puede ver.
 */
const informes = createRoute({
  getParentRoute: () => panel,
  path: '/informes',
  component: MarcoDeInformes,
});

const informesIndice = createRoute({
  getParentRoute: () => informes,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/informes/asistencia' });
  },
});

const informeDeAsistencia = createRoute({
  getParentRoute: () => informes,
  path: 'asistencia',
  component: function PantallaDeAsistencia() {
    const { club } = useContextoDelClub();
    return <Asistencia club={club} />;
  },
});

const informeDeIngresos = createRoute({
  getParentRoute: () => informes,
  path: 'ingresos',
  component: function PantallaDeIngresos() {
    const { club, rol } = useContextoDelClub();

    /* El marco deja pasar al entrenador —la asistencia es suya— y esto no lo es. */
    if (!puedeVerInformesDeGestion(rol))
      return <Navigate to="/informes/asistencia" replace />;

    return <Ingresos club={club} />;
  },
});

/*
 * Los cinco informes de gestión de fase 2.
 *
 * Comparten el guardián porque comparten el permiso: los ocho informes que no son la
 * asistencia declaran `own` y `adm`. Se escribe una vez y se aplica cinco veces, en
 * lugar de cinco copias del mismo `if` que un día no coinciden.
 */
const informeDeGestion = (
  ruta: string,
  Pantalla: (props: { club: string }) => ReactNode,
  funcion?: FuncionDelPlan
) =>
  createRoute({
    getParentRoute: () => informes,
    path: ruta,
    component: function PantallaDeInformeDeGestion() {
      const { club, rol, tiene } = useContextoDelClub();

      if (!puedeVerInformesDeGestion(rol))
        return <Navigate to="/informes/asistencia" replace />;

      // El rol primero y el plan después: son dos preguntas (§A.2.1).
      if (funcion && !tiene(funcion)) return <NoIncluido funcion={funcion} />;

      return <Pantalla club={club} />;
    },
  });

const informeDeBajas = informeDeGestion('bajas', Bajas);
const informeDeRentabilidad = informeDeGestion('rentabilidad', Rentabilidad);
const informeDeOcupacion = informeDeGestion('ocupacion', Ocupacion);
const informeDePrevision = informeDeGestion('prevision', PrevisionDeCaja);
const informeDeHoras = informeDeGestion('horas', HorasDeEntrenador);
const informeDeComparativa = informeDeGestion(
  'comparativa',
  Comparativa,
  'benchmarking'
);

/*
 * El embudo va escrito a mano y no con `informeDeGestion`: es el único que navega a
 * otra sección —a la ficha del buscador, que es lo que arregla su estado vacío— y la
 * ayuda solo sabe pasar el club. Añadirle un parámetro opcional para un caso sería
 * hacer genérica una función de cinco líneas.
 */
const informeDeEmbudo = createRoute({
  getParentRoute: () => informes,
  path: 'embudo',
  component: function PantallaDelEmbudo() {
    const { club, rol } = useContextoDelClub();
    const ir = useNavigate();

    if (!puedeVerInformesDeGestion(rol))
      return <Navigate to="/informes/asistencia" replace />;

    return (
      <Embudo
        club={club}
        alAbrirLaFicha={() => void ir({ to: '/club/ficha' })}
      />
    );
  },
});

/* ── §P.11 · Material ─────────────────────────────────────────────────────── */

const material = createRoute({
  getParentRoute: () => panel,
  path: '/material',
  component: MarcoDeMaterial,
});

const materialIndice = createRoute({
  getParentRoute: () => material,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/material/catalogo' });
  },
});

const materialCatalogo = createRoute({
  getParentRoute: () => material,
  path: 'catalogo',
  component: function PantallaDeCatalogo() {
    const { club, rol, suspendido } = useContextoDelClub();
    return (
      <Catalogo
        club={club}
        puedeEditar={puedeEditarMaterial(rol) && !suspendido}
      />
    );
  },
});

const materialPedidos = createRoute({
  getParentRoute: () => material,
  path: 'pedidos',
  component: function PantallaDePedidos() {
    const { club, rol, suspendido } = useContextoDelClub();
    return (
      <Pedidos
        club={club}
        puedeEditar={puedeEditarMaterial(rol) && !suspendido}
      />
    );
  },
});

const materialSolicitudes = createRoute({
  getParentRoute: () => material,
  path: 'solicitudes',
  component: function PantallaDeSolicitudes() {
    const { club, rol, suspendido } = useContextoDelClub();
    const ir = useNavigate();
    return (
      <Solicitudes
        club={club}
        puedeEditar={puedeEditarMaterial(rol) && !suspendido}
        alAbrirAlumno={(id) =>
          void ir({ to: '/alumnos/$alumnoId', params: { alumnoId: id } })
        }
      />
    );
  },
});

const materialSegundaMano = createRoute({
  getParentRoute: () => material,
  path: 'segunda-mano',
  component: function PantallaDeSegundaMano() {
    const { club, rol, suspendido } = useContextoDelClub();
    return (
      <SegundaMano
        club={club}
        puedeEditar={puedeEditarMaterial(rol) && !suspendido}
      />
    );
  },
});

/* ── §P.10 · Competición ──────────────────────────────────────────────────── */

const competicion = createRoute({
  getParentRoute: () => panel,
  path: '/competicion',
  component: MarcoDeCompeticion,
});

const competicionIndice = createRoute({
  getParentRoute: () => competicion,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/competicion/campeonatos' });
  },
});

const campeonatos = createRoute({
  getParentRoute: () => competicion,
  path: 'campeonatos',
  component: function PantallaDeCampeonatos() {
    const { club, rol, suspendido, tiene } = useContextoDelClub();
    const ir = useNavigate();
    // Las licencias, la otra pestaña, no son del plan: siguen abiertas (§A.2.1).
    if (!tiene('campeonatos')) return <NoIncluido funcion="campeonatos" />;
    return (
      <Campeonatos
        club={club}
        puedeEditar={puedeVerCompeticion(rol) && !suspendido}
        alAbrir={(id) =>
          void ir({
            to: '/competicion/campeonatos/$campeonatoId',
            params: { campeonatoId: id },
          })
        }
      />
    );
  },
});

/*
 * La ficha cuelga de la pestaña y no del marco: se llega desde la lista y se vuelve
 * a ella, así que compartir el enlace tiene que abrir el campeonato con sus pestañas
 * arriba, igual que un recibo dentro de «Cobros».
 */
const fichaDeCampeonato = createRoute({
  getParentRoute: () => competicion,
  path: 'campeonatos/$campeonatoId',
  component: function PantallaDeFichaDeCampeonato() {
    const { club, rol, suspendido, tiene } = useContextoDelClub();
    const { campeonatoId } = useParams({
      from: '/panel/competicion/campeonatos/$campeonatoId',
    });
    const ir = useNavigate();
    if (!tiene('campeonatos')) return <NoIncluido funcion="campeonatos" />;
    return (
      <FichaDeCampeonato
        club={club}
        campeonatoId={campeonatoId}
        puedeEditar={puedeVerCompeticion(rol) && !suspendido}
        alCerrar={() => void ir({ to: '/competicion/campeonatos' })}
      />
    );
  },
});

const licencias = createRoute({
  getParentRoute: () => competicion,
  path: 'licencias',
  component: function PantallaDeLicencias() {
    const { club, rol, suspendido } = useContextoDelClub();

    /*
     * El segundo guardián, y no sobra: el marco deja pasar al entrenador —los
     * campeonatos son suyos— y esta pestaña no lo es. Sin esto, escribir
     * `/competicion/licencias` en la barra se la abre.
     */
    if (!puedeVerLicencias(rol))
      return <Navigate to="/competicion/campeonatos" replace />;

    return <Licencias club={club} puedeEditar={!suspendido} />;
  },
});

/* ── El árbol ─────────────────────────────────────────────────────────────── */

const arbol = raiz.addChildren([
  puerta,
  acceso,
  invitacion,
  restablecer,
  verificarCorreo,
  miCuenta,
  panel.addChildren([
    inicio,
    alumnos.addChildren([listaDeAlumnos, clasesDePrueba]),
    fichaDeAlumno,
    material.addChildren([
      materialIndice,
      materialCatalogo,
      materialPedidos,
      materialSolicitudes,
      materialSegundaMano,
    ]),
    competicion.addChildren([
      competicionIndice,
      campeonatos,
      fichaDeCampeonato,
      licencias,
    ]),
    grupos,
    fichaDeGrupo,
    calendario.addChildren([
      calendarioIndice,
      clases,
      asistenciaDelCalendario,
      ausencias,
    ]),
    avisos.addChildren([
      avisosIndice,
      avisosPublicados,
      acusesDeAviso,
      chat,
      muro,
      silencio,
    ]),
    grados.addChildren([
      gradosIndice,
      sistemasDeGrados,
      escalera,
      examenes,
      fichaDeExamen,
      temario,
    ]),
    cobros.addChildren([
      cobrosIndice,
      recibos,
      fichaDeRecibo,
      morosos,
      loCobrado,
      remesa,
      tarifas,
      descuentos,
      devoluciones,
    ]),
    pagadores,
    fichaDePagador,
    elClub.addChildren([
      elClubIndice,
      clubDatos,
      clubMarca,
      clubAjustes,
      clubSedes,
      clubDisciplinas,
      clubFicha,
      clubPersonal,
    ]),
    cumplimiento.addChildren([
      cumplimientoIndice,
      consentimientos,
      solicitudesDeDatos,
      accesos,
      soporte,
      contrato,
      exportarTodo,
    ]),
    informes.addChildren([
      informesIndice,
      informeDeAsistencia,
      informeDeIngresos,
      informeDeBajas,
      informeDeRentabilidad,
      informeDeOcupacion,
      informeDePrevision,
      informeDeHoras,
      informeDeEmbudo,
      informeDeComparativa,
    ]),
  ]),
  admin.addChildren([
    adminRaiz,
    adminHoy,
    adminClubes,
    adminFichaDeClub,
    adminDinero.addChildren([
      adminDineroIndice,
      adminAvisosDePlanes,
      adminImpagados,
      adminPlanes,
      adminMetricas,
    ]),
    adminSoporte.addChildren([
      adminSoporteIndice,
      adminPersonas,
      adminPersona,
      adminAvisos,
      adminSalud,
      adminAccesos,
    ]),
    adminOperacion.addChildren([
      adminOperacionIndice,
      adminEstado,
      adminProcesos,
      adminWebhooks,
      adminDatos,
      adminInterruptores,
      adminEquipo,
    ]),
    adminComercial,
    adminCatalogos.addChildren([
      adminCatalogosIndice,
      adminDisciplinas,
      adminPlantillas,
      adminConsentimientos,
    ]),
    adminDirectorio.addChildren([
      adminDirectorioIndice,
      adminReclamaciones,
      adminBajasDelDirectorio,
      adminImportar,
      adminComparativas,
    ]),
  ]),
]);

export const enrutador = createRouter({
  routeTree: arbol,
  /*
   * Una dirección que no existe lleva a la puerta, no a una pantalla en blanco.
   * Es la misma regla que tenía `aSeccion`: un enlace viejo o mal copiado no puede
   * dejar la aplicación sin pintar nada.
   */
  defaultNotFoundComponent: Puerta,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof enrutador;
  }
}
